import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAdminToken } from "../../utils/adminAuth";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { adminGet } from "../../api/adminClient";

/**
 * Protects admin routes: redirect to /admin/login if not authenticated.
 * Validates session on mount (DB reset/seed invalidates tokens - redirect cleanly).
 * Auth = token (primary). User can be null after refresh; token is source of truth.
 */
const AdminProtectedRoute = ({ children, requiredPermission, requiredRole }) => {
  const location = useLocation();
  const token = getAdminToken();
  const { checkPermission, checkRole } = useAdminAuth();
  const [validating, setValidating] = useState(Boolean(token));
  const [valid, setValid] = useState(!token ? false : null);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValid(false);
      return;
    }
    let cancelled = false;
    adminGet("/admin/auth/profile")
      .then((data) => {
        if (!cancelled) {
          setValid(data != null);
          setValidating(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setValid(false);
          setValidating(false);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  if (!token) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (validating) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
          <p className="text-sm text-muted-foreground">Validating session...</p>
        </div>
      </div>
    );
  }

  if (valid === false) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (requiredPermission && !checkPermission(requiredPermission)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (requiredRole && !checkRole(requiredRole)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

export default AdminProtectedRoute;
